import { Plus, Send } from 'lucide-react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../api';
import { numeroALetras } from '../numeroALetras';
import { calcularTextoConRelleno } from '../rellenoTexto';
import ChequeLienzo from './ChequeLienzo';
import ProveedorForm from './ProveedorForm';

export default function ChequeGenerator() {
  const [bancos, setBancos] = useState([]);
  const [bancoId, setBancoId] = useState(null);
  const [calibracion, setCalibracion] = useState(null);

  const [proveedores, setProveedores] = useState([]);
  const [proveedorId, setProveedorId] = useState('');
  const [mostrarNuevoProveedor, setMostrarNuevoProveedor] = useState(false);

  const [fecha, setFecha] = useState('');
  const [ciudad, setCiudad] = useState(localStorage.getItem('hf_cheques_ciudad') || '');
  const [concepto, setConcepto] = useState('');
  const [montoNumero, setMontoNumero] = useState('');
  const [numeroCheque, setNumeroCheque] = useState('');
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    api.get('/bancos/').then((res) => {
      setBancos(res.data);
      if (res.data.length > 0) setBancoId(res.data[0].id);
    });
    cargarProveedores();
  }, []);

  function cargarProveedores() {
    api.get('/proveedores/').then((res) => setProveedores(res.data.filter((p) => p.activo)));
  }

  useEffect(() => {
    if (!bancoId) return;
    api.get(`/bancos/${bancoId}/plantilla/`).then((res) => setCalibracion(res.data));
  }, [bancoId]);

  useEffect(() => {
    localStorage.setItem('hf_cheques_ciudad', ciudad);
  }, [ciudad]);

  function alCrearProveedor(proveedor) {
    setProveedores((prev) => [...prev, proveedor].sort((a, b) => a.nombre.localeCompare(b.nombre)));
    setProveedorId(proveedor.id);
    setMostrarNuevoProveedor(false);
  }

  const banco = bancos.find((b) => b.id === bancoId);
  const proveedor = proveedores.find((p) => p.id === Number(proveedorId));
  const beneficiario = proveedor?.nombre || '';
  const monto = Number(montoNumero) || 0;
  const montoFormateado = monto.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fechaTexto = fecha ? `${ciudad ? ciudad + ' ' : ''}${fecha}` : '';
  const montoTextoBase = numeroALetras(monto);

  const { textoFinal: montoTextoConRelleno, desborda } = calibracion
    ? calcularTextoConRelleno({
        texto: montoTextoBase,
        relleno: calibracion.relleno_char,
        fuentePt: Number(calibracion.fuente_pt),
        anchoMm: Number(calibracion.texto_w),
      })
    : { textoFinal: montoTextoBase, desborda: false };

  const formularioValido =
    bancoId && numeroCheque.trim() && ciudad.trim() && fecha && proveedorId && concepto.trim() && monto > 0;

  async function handleSolicitar(e) {
    e.preventDefault();
    if (!formularioValido) {
      toast.error('Completa todos los campos (número de cheque, ciudad, fecha, proveedor, concepto y un monto mayor a cero) antes de solicitar.');
      return;
    }
    setGuardando(true);
    try {
      // Toda solicitud queda pendiente de aprobación — esta pantalla nunca
      // imprime, ni siquiera para Aprobador/Admin. Recién se puede imprimir
      // desde "Aprobaciones" o "Historial" una vez aprobada.
      await api.post('/cheques/', {
        banco: bancoId,
        fecha_emision: fecha,
        ciudad: ciudad.trim(),
        proveedor: proveedorId,
        beneficiario,
        concepto: concepto.trim(),
        monto_numero: monto.toFixed(2),
        numero_cheque: numeroCheque,
      });
      toast.success('Solicitud enviada. Queda pendiente de aprobación.');
      setProveedorId('');
      setConcepto('');
      setMontoNumero('');
      setNumeroCheque('');
    } catch (err) {
      const detalle = err.response?.data;
      const mensaje = detalle ? Object.values(detalle).flat().join(' ') : 'No se pudo enviar la solicitud.';
      toast.error(mensaje);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="generador">
      <div className="panel-controles no-imprimir">
        <div className="control-group">
          <label>Banco</label>
          <select value={bancoId || ''} onChange={(e) => setBancoId(Number(e.target.value))} required>
            {bancos.map((b) => (
              <option key={b.id} value={b.id}>{b.nombre}</option>
            ))}
          </select>

          <div className="coord-inputs">
            <div>
              <label>N.º de cheque</label>
              <input value={numeroCheque} onChange={(e) => setNumeroCheque(e.target.value)} required />
            </div>
            <div>
              <label>Ciudad (para la fecha)</label>
              <input value={ciudad} onChange={(e) => setCiudad(e.target.value)} placeholder="Ej. Guayaquil" required />
            </div>
          </div>

          <div className="coord-inputs">
            <div>
              <label>Fecha de emisión</label>
              <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} required />
            </div>
            <div>
              <label>Valor numérico ($)</label>
              <input type="number" step="0.01" min="0.01" value={montoNumero} onChange={(e) => setMontoNumero(e.target.value)} required />
            </div>
          </div>

          <label>Páguese a la orden de</label>
          <select value={proveedorId} onChange={(e) => setProveedorId(e.target.value)} required>
            <option value="">-- Selecciona un proveedor --</option>
            {proveedores.map((p) => (
              <option key={p.id} value={p.id}>{p.nombre}</option>
            ))}
          </select>
          {!mostrarNuevoProveedor ? (
            <button type="button" className="btn-enlace" onClick={() => setMostrarNuevoProveedor(true)}>
              <Plus size={14} /> Agregar proveedor
            </button>
          ) : (
            <ProveedorForm onGuardado={alCrearProveedor} onCancelar={() => setMostrarNuevoProveedor(false)} />
          )}

          <label>Concepto</label>
          <textarea
            value={concepto}
            onChange={(e) => setConcepto(e.target.value)}
            placeholder="Describe los detalles del pago"
            rows={3}
            required
          />
        </div>

        {desborda && (
          <p className="advertencia">
            El monto en letras no cabe en el espacio calibrado con el tamaño de letra actual.
            Ve a "Configuración → Calibración" y reduce el tamaño de letra o amplía el ancho de línea.
          </p>
        )}

        <button className="btn-primary" onClick={handleSolicitar} disabled={!formularioValido || guardando}>
          <Send size={16} /> {guardando ? 'Enviando…' : 'Solicitar aprobación'}
        </button>
      </div>

      <div className="preview-area no-imprimir-fondo">
        <ChequeLienzo
          banco={banco}
          calibracion={calibracion}
          fecha={fechaTexto}
          nombre={beneficiario}
          montoFormateado={montoFormateado}
          montoTexto={montoTextoConRelleno}
        />
      </div>
    </div>
  );
}
